import type { Contract } from '../types';

import dayjs from 'dayjs';
import { clamp } from 'lodash';

import { ClaimMethod } from '@paid-ui/enums/claim';
import { VariationStage } from '@paid-ui/enums/variation';
import { WorkItemStatus } from '@paid-ui/enums/work-item';
import { getConstructionDays } from '@paid-ui/utils/contract';

import { contractManager } from '../model';

const formatDate = (date?: string) => (date ? dayjs(date) : null);

export const saveTimeline = (contract?: Contract) => {
  if (!contract) return;

  const {
    paymentStructure,
    anticipatedCommencementDate,
    commencementDate,
    anticipatedCompletionDate: anticipatedCompletionDateFromApi,
    contractualCompletionDate: contractualCompletionDateFromApi,
    actualCompletionDate,
    initialConstructionDays,
    inclementWeather,
    weekendDays,
    weekdayPublicHolidays,
    rosteredDaysOff,
    foreseeableBreaks,
    builderHolidays,
    additionalDaysFromVariations,
    reasonableDelays,
    variations = [],
    variationWorkItems = [],
  } = contract;

  const constructionDays = getConstructionDays(contract);

  const anticipatedCompletionDate = anticipatedCompletionDateFromApi
    ? formatDate(anticipatedCompletionDateFromApi)
    : anticipatedCommencementDate
      ? dayjs(anticipatedCommencementDate).add(constructionDays, 'days')
      : null;

  const contractualCompletionDate = contractualCompletionDateFromApi
    ? formatDate(contractualCompletionDateFromApi)
    : commencementDate
      ? dayjs(commencementDate).add(constructionDays, 'days')
      : null;

  contractManager.inclementWeather = inclementWeather ?? 0;
  contractManager.weekendDays = weekendDays ?? 0;
  contractManager.weekdayPublicHolidays = weekdayPublicHolidays ?? 0;
  contractManager.rosteredDaysOff = rosteredDaysOff ?? 0;
  contractManager.foreseeableBreaks = foreseeableBreaks ?? 0;
  contractManager.builderHolidays = builderHolidays ?? 0;
  contractManager.reasonableDelays = reasonableDelays ?? null;
  contractManager.additionalDaysFromVariations = additionalDaysFromVariations ?? 0;
  contractManager.totalTimeExtension =
    paymentStructure === ClaimMethod.PERIODIC
      ? variationWorkItems.reduce(
          (result, { workItemStatus, timeExtension }) =>
            result +
            (workItemStatus &&
            [WorkItemStatus.APPROVED, WorkItemStatus.COMPLETED].includes(workItemStatus)
              ? (timeExtension?.days ?? 0)
              : 0),
          0,
        )
      : variations.reduce(
          (result, { variationStage, reasonableDelayDays = 0 }) =>
            result +
            (variationStage &&
            [VariationStage.APPROVED, VariationStage.COMPLETED].includes(variationStage)
              ? reasonableDelayDays
              : 0),
          0,
        );

  contractManager.hasWorkCommenced = Boolean(commencementDate);
  contractManager.hasWorkCompleted = Boolean(actualCompletionDate);
  contractManager.anticipatedCommencementDate = formatDate(anticipatedCommencementDate);
  contractManager.commencementDate = formatDate(commencementDate);
  contractManager.actualCompletionDate = formatDate(actualCompletionDate);
  contractManager.initialConstructionDays = initialConstructionDays ?? 0;
  contractManager.anticipatedCompletionDate = anticipatedCompletionDate;
  contractManager.contractualCompletionDate = contractualCompletionDate;
  contractManager.constructionDays =
    commencementDate && actualCompletionDate
      ? dayjs(actualCompletionDate).diff(dayjs(commencementDate), 'days')
      : constructionDays;

  if (!commencementDate) {
    contractManager.overdueDays =
      anticipatedCompletionDate?.diff(dayjs().startOf('day'), 'days') ?? 0;
  } else if (actualCompletionDate) {
    contractManager.overdueDays =
      contractualCompletionDate?.diff(actualCompletionDate, 'days') ?? 0;
  } else {
    contractManager.overdueDays =
      contractualCompletionDate?.diff(dayjs().startOf('day'), 'days') ?? 0;
  }

  const startDate = commencementDate ?? anticipatedCommencementDate;

  if (!startDate) {
    contractManager.remainingDays = constructionDays;
  } else if (dayjs(startDate).isAfter(dayjs().endOf('day'))) {
    contractManager.remainingDays = constructionDays;
  } else {
    const pastDays = dayjs().startOf('day').diff(startDate, 'days') + 1;
    contractManager.remainingDays = clamp(constructionDays - pastDays, 0, constructionDays);
  }
};
